import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  TrendingUp, IndianRupee, Globe, Download, Receipt, AlertCircle, Sparkles,
} from "lucide-react";

/**
 * Sprint 4 — Revenue Dashboard (admin).
 */
export default function RevenueDashboardPage() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/billing/revenue/summary").then((r) => setSummary(r.data))
      .catch((e) => toast.error("Could not load revenue"));
  }, []);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/billing/revenue/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "diplomexa-revenue.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="revenue-dashboard-root">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Revenue Dashboard
            </p>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-[#0A192F]">
              Diplomexa revenue at a glance
            </h1>
          </div>
          <button type="button" onClick={downloadCsv}
                  className="px-4 py-2 rounded-md bg-[#0F4C81] text-white text-sm flex items-center gap-2"
                  data-testid="revenue-export-csv">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Kpi label="Total INR" value={`₹${summary.total_inr.toLocaleString()}`} icon={IndianRupee} testid="kpi-total-inr" />
              <Kpi label="GST collected" value={`₹${summary.gst_collected.toLocaleString()}`} icon={Receipt} testid="kpi-gst" />
              <Kpi label="VAT collected" value={summary.vat_collected ? `${summary.vat_collected}` : "0"} icon={Receipt} testid="kpi-vat" tone="muted" />
              <Kpi label="Paid" value={summary.paid_count} icon={TrendingUp} tone="success" testid="kpi-paid" />
              <Kpi label="Pending" value={summary.pending_count} icon={AlertCircle} tone="warn" testid="kpi-pending" />
              <Kpi label="Failed" value={summary.failed_count} icon={AlertCircle} tone="danger" testid="kpi-failed" />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Card title="International revenue by currency" testid="card-intl-currency">
                {Object.keys(summary.total_intl_by_currency).length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No international transactions yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(summary.total_intl_by_currency).map(([k, v]) => (
                        <tr key={k} className="border-t border-slate-100">
                          <td className="py-1.5 flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-[#0F4C81]" /> {k}</td>
                          <td className="py-1.5 text-right font-medium">{Number(v).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
              <Card title="Revenue by plan" testid="card-by-plan">
                <Bars data={summary.by_plan} />
              </Card>
              <Card title="Revenue by event" testid="card-by-event">
                <Bars data={summary.by_event} />
              </Card>
            </div>

            <p className="text-xs text-slate-500 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
              Card data is never stored by Diplomexa. All amounts are recomputed
              server-side; the frontend currency toggle is cosmetic. Tax breakups
              follow the configured GST / VAT profile.
            </p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
  const ring = tone === "success" ? "border-emerald-200 bg-emerald-50"
            : tone === "warn"    ? "border-amber-200 bg-amber-50"
            : tone === "danger"  ? "border-rose-200 bg-rose-50"
            : tone === "muted"   ? "border-slate-200 bg-slate-50"
            : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border ${ring} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <Icon className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <p className="mt-1.5 text-xl font-bold text-[#0A192F]">{value}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3">{title}</p>
      {children}
    </div>
  );
}

function Bars({ data }) {
  const entries = Object.entries(data || {}).slice(0, 6);
  if (entries.length === 0) return <p className="text-xs text-slate-500 italic">No data yet.</p>;
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-700 truncate max-w-[60%]">{k}</span>
            <span className="font-medium text-[#0A192F]">{Number(v).toLocaleString()}</span>
          </div>
          <div className="mt-0.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                 style={{ width: `${(v / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
